import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Copy, Phone, Star } from "lucide-react";
import { toast } from "sonner";
import { PublicHeader } from "@/components/PublicHeader";
import { ReviewDialog } from "@/components/ReviewDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";

export default function Confirmation() {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/bookings/${id}/confirmation`).then((r) => setBooking(r.data)).catch(() => setError(true));
    api.get("/config/public").then((r) => setConfig(r.data)).catch(() => {});
  }, [id]);

  const copy = (text, label) => {
    navigator.clipboard?.writeText(text).then(() => toast.success(`${label} copied`));
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background" data-testid="confirmation-error">
        <PublicHeader />
        <div className="max-w-xl mx-auto px-4 py-24 text-center">
          <p className="text-muted-foreground">We could not find that booking. Please call us and we will sort it out.</p>
        </div>
      </div>
    );
  }

  if (!booking || !config) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="confirmation-loading">
        <div className="h-10 w-10 rounded-full border-4 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="noise-overlay min-h-screen bg-background" data-testid="confirmation-page">
      <PublicHeader phoneDisplay={config.phone_display} phoneTel={config.phone_tel} />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16">
        <div className="text-center mb-10 animate-fade-up">
          <span className="inline-flex h-16 w-16 rounded-full bg-secondary items-center justify-center mb-4">
            <CheckCircle2 className="h-9 w-9 text-leaf" />
          </span>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-semibold">Booking received</h1>
          <p className="text-muted-foreground mt-3">
            Thanks {booking.name.split(" ")[0]} — a confirmation email is on its way to you. Your booking reference is{" "}
            <strong className="text-foreground" data-testid="confirmation-ref">{booking.ref}</strong>.
          </p>
        </div>

        <Card className="rounded-2xl border-border mb-6 animate-fade-up" style={{ animationDelay: "0.1s" }}>
          <CardContent className="p-6 sm:p-8">
            <h2 className="text-lg font-medium mb-4">Your booking</h2>
            <dl className="text-sm space-y-3" data-testid="confirmation-details">
              <div className="flex justify-between"><dt className="text-muted-foreground">Service</dt><dd className="font-semibold">{booking.service_name}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Date</dt><dd className="font-semibold" data-testid="confirmation-date">{booking.date || "To be confirmed"}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Time</dt><dd className="font-semibold">{booking.time_slot || "To be confirmed"}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Address</dt><dd className="font-semibold text-right">{booking.address}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Price</dt><dd className="font-semibold" data-testid="confirmation-price">{booking.final_price != null ? `$${booking.final_price}` : "To be confirmed"}</dd></div>
              {booking.customer_estimate != null && (
                <div className="flex justify-between"><dt className="text-muted-foreground">Your estimate</dt><dd className="font-semibold" data-testid="confirmation-estimate">${booking.customer_estimate}</dd></div>
              )}
            </dl>
            {!booking.date && (
              <p className="text-sm text-muted-foreground mt-4" data-testid="confirmation-timing-note">
                We&apos;ll be in touch shortly to lock in a date and time that suits you.
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-border mb-6 animate-fade-up" style={{ animationDelay: "0.2s" }} data-testid="confirmation-payment-card">
          <CardContent className="p-6 sm:p-8">
            {booking.payment_status === "paid" ? (
              <>
                <h2 className="text-lg font-medium mb-2">Payment received</h2>
                <p className="text-sm text-muted-foreground">
                  Thanks {booking.name.split(" ")[0]} — your payment has been received. See you on {booking.date}.
                </p>
              </>
            ) : (
              <>
                <h2 className="text-lg font-medium mb-2" data-testid="payment-amount-heading">
                  {booking.payment_choice === "deposit"
                    ? `Deposit due: $${booking.amount_due}`
                    : booking.payment_choice === "full"
                      ? `Payment due: $${booking.final_price ?? booking.amount_due}`
                      : `To pay on completion: $${booking.final_price ?? booking.estimated_price}`}
                </h2>
                {booking.payment_choice === "deposit" && booking.final_price != null && (
                  <p className="text-sm font-semibold mb-2" data-testid="payment-balance-line">
                    Balance due on completion: ${Math.max(booking.final_price - booking.amount_due, 0)}
                  </p>
                )}
                <p className="text-sm text-muted-foreground mb-5">
                  Transfer to the account below using your booking reference so we can match your payment.
                </p>
                <div className="bg-secondary rounded-xl p-5 text-sm space-y-3">
                  {[
                    ["Account name", config.bank.account_name],
                    ["BSB", config.bank.bsb],
                    ["Account number", config.bank.account],
                    ["Reference", booking.ref],
                  ].map(([label, value]) => (
                    <div key={label} className="flex justify-between items-center gap-4">
                      <span className="text-muted-foreground">{label}</span>
                      <span className="flex items-center gap-2 font-semibold">
                        {value}
                        <button type="button" onClick={() => copy(value, label)} data-testid={`copy-${label.toLowerCase().replace(/ /g, "-")}`}
                          className="text-muted-foreground hover:text-primary transition-colors">
                          <Copy className="h-4 w-4" />
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-border mb-6 animate-fade-up" style={{ animationDelay: "0.25s" }} data-testid="confirmation-review-card">
          <CardContent className="p-6 sm:p-8 text-center">
            <h2 className="text-lg font-medium mb-2">Would you mind leaving a review?</h2>
            <p className="text-sm text-muted-foreground mb-5">
              Once your job is done, would you mind spending just 1 minute leaving a review?
              It would be greatly appreciated — it really helps our small local business.
            </p>
            <ReviewDialog
              trigger={
                <Button variant="outline" className="rounded-full border-primary text-primary hover:bg-primary hover:text-white transition-colors" data-testid="confirmation-review-button">
                  <Star className="mr-2 h-4 w-4" /> Leave a quick review
                </Button>
              }
            />
          </CardContent>
        </Card>

        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-up" style={{ animationDelay: "0.3s" }}>
          <a href={`tel:${config.phone_tel}`} data-testid="confirmation-call-button">
            <Button variant="outline" className="rounded-full w-full sm:w-auto">
              <Phone className="mr-2 h-4 w-4" /> Questions? Call {config.phone_display}
            </Button>
          </a>
          <Link to="/" data-testid="confirmation-home-link">
            <Button className="rounded-full bg-primary hover:bg-[#21421D] text-white w-full sm:w-auto transition-colors">Back to home</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
